import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import favoritesService from '../services/favoritesService';
import { 
Timestamp } from 'firebase/firestore';
import { toast } from '@/components/ui/use-toast';
import { Category } from '@/types/category';

interface CategoryContextType {
  categories: Category[];
  loading: boolean;
  refreshCategories: () => Promise<void>;
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

export function CategoryProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCategories = useCallback(async () => {
    if (!user?.uid) {
      setCategories([]);
      setLoading(false);
      return;
    }

    try {
      console.log('Loading categories for user:', user.uid);
      const userCategories = await favoritesService.getCategories(user.uid);
      // Convert Timestamp to Date and ensure required fields
      const categoriesWithDate = userCategories.map(cat => ({
        ...cat,
        createdAt: cat.createdAt instanceof Timestamp ? cat.createdAt.toDate() : cat.createdAt,
        isPublic: cat.isPublic || false
      }));
      console.log('Loaded categories:', categoriesWithDate);
      setCategories(categoriesWithDate);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast({
        title: "Error",
        description: "Failed to load categories",
        variant: "destructive",
      });
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const value = {
    categories,
    loading,
    refreshCategories: loadCategories
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
}

export function useCategories() {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
}